import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface CSSHolographicCardProps {
  imageUrl: string;
  className?: string;
  style?: 'rainbow' | 'neon' | 'chrome' | 'galaxy';
  intensity?: number;
  overlayData?: {
    author: string;
    title: string;
    serial: string;
    rarity: string;
  };
}

export const CSSHolographicCard: React.FC<CSSHolographicCardProps> = ({
  imageUrl,
  className,
  style = 'rainbow',
  intensity = 0.6,
  overlayData
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageError(true);
    img.src = imageUrl;
  }, [imageUrl]);

  const getHolographicStyles = () => {
    const baseStyles = {
      rainbow: 'linear-gradient(45deg, #ff0080, #00ffff, #00ff00, #ffff00, #ff8000, #ff0000)',
      neon: 'linear-gradient(45deg, #00ffff, #ff00ff, #00ff80, #8000ff)',
      chrome: 'linear-gradient(45deg, #c0c0c0, #ffffff, #c0c0c0, #808080)',
      galaxy: 'linear-gradient(45deg, #4a0080, #8000ff, #ff0080, #00ffff, #0080ff)'
    };
    return baseStyles[style];
  };

  if (imageError) {
    return (
      <div className={cn(
        "relative w-full aspect-[1/2] rounded-xl overflow-hidden border bg-muted flex items-center justify-center",
        className
      )}>
        <p className="text-muted-foreground text-sm">Failed to load image</p>
      </div>
    );
  }

  return (
    <div 
      className={cn(
        "relative w-full aspect-[1/2] rounded-xl overflow-hidden border cursor-pointer group",
        "transform-gpu transition-all duration-300 ease-out",
        "hover:scale-105 hover:rotate-1",
        className
      )}
      style={{
        perspective: '1000px',
      }}
    >
      {/* Base card container */}
      <div 
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          transform: 'rotateY(0deg) rotateX(0deg)',
          animation: 'holographic-float 6s ease-in-out infinite',
        }}
      >
        {/* Main image */}
        <div className="absolute inset-0 rounded-xl overflow-hidden">
          {imageLoaded ? (
            <img
              src={imageUrl}
              alt="Holographic card"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              style={{
                filter: 'brightness(1.1) contrast(1.2) saturate(1.1)',
              }}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-muted/50 to-muted flex items-center justify-center">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          )}
        </div>

        {/* Holographic overlay */}
        <div 
          className="absolute inset-0 rounded-xl"
          style={{
            background: getHolographicStyles(),
            opacity: intensity * 0.4,
            mixBlendMode: 'overlay',
            animation: 'holographic-shift 3s ease-in-out infinite alternate',
          }}
        />

        {/* Shimmer effect */}
        <div 
          className="absolute inset-0 rounded-xl"
          style={{
            background: `linear-gradient(
              45deg,
              transparent 30%,
              rgba(255, 255, 255, 0.5) 50%,
              transparent 70%
            )`,
            animation: 'shimmer 2s ease-in-out infinite',
            transform: 'translateX(-100%)',
          }}
        />

        {/* Prismatic reflections */}
        <div className="absolute inset-0 rounded-xl opacity-60">
          <div 
            className="absolute top-0 left-0 w-full h-1/3"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 100%)',
              animation: 'prism-glow 4s ease-in-out infinite alternate',
            }}
          />
          <div 
            className="absolute bottom-0 right-0 w-1/3 h-full"
            style={{
              background: 'linear-gradient(270deg, rgba(0,255,255,0.2) 0%, transparent 100%)',
              animation: 'prism-glow 4s ease-in-out infinite alternate-reverse',
            }}
          />
        </div>

        {/* Edge glow */}
        <div 
          className="absolute inset-0 rounded-xl"
          style={{
            boxShadow: `
              inset 0 0 20px rgba(255, 255, 255, 0.2),
              0 0 30px rgba(255, 255, 255, 0.1),
              0 0 60px rgba(255, 255, 255, 0.05)
            `,
            animation: 'edge-glow 3s ease-in-out infinite alternate',
          }}
        />

        {/* Overlay Information */}
        {overlayData && (
          <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-black/80 via-transparent to-transparent">
            {/* Top overlay - Author and Serial */}
            <div className="absolute top-0 left-0 right-0 p-3 bg-gradient-to-b from-black/60 to-transparent">
              <div className="flex justify-between items-start text-white">
                <div className="text-xs opacity-90">
                  by <span className="font-medium">{overlayData.author}</span>
                </div>
                <div className="text-xs font-mono bg-black/50 px-2 py-1 rounded-full">
                  {overlayData.serial}
                </div>
              </div>
            </div>

            {/* Bottom overlay - Title and Rarity */}
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <div className="text-white">
                <h3 className="font-bold text-sm mb-1 truncate">{overlayData.title}</h3>
                <div className="flex justify-between items-center">
                  <span 
                    className={`text-xs px-2 py-1 rounded-full font-medium uppercase tracking-wide ${
                      overlayData.rarity === 'common' ? 'bg-emerald-600/80 text-emerald-100 border border-emerald-500/50' :
                      overlayData.rarity === 'uncommon' ? 'bg-blue-600/80 text-blue-100 border border-blue-500/50' :
                      overlayData.rarity === 'rare' ? 'bg-purple-600/80 text-purple-100 border border-purple-500/50' :
                      'bg-orange-600/80 text-orange-100 border border-orange-500/50'
                    }`}
                  >
                    {overlayData.rarity}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes holographic-float {
            0%, 100% { transform: rotateY(-2deg) rotateX(1deg) translateY(0px); }
            25% { transform: rotateY(2deg) rotateX(-1deg) translateY(-2px); }
            50% { transform: rotateY(1deg) rotateX(2deg) translateY(0px); }
            75% { transform: rotateY(-1deg) rotateX(-2deg) translateY(-1px); }
          }

          @keyframes holographic-shift {
            0% { transform: translateX(-2px) translateY(-1px) scale(1); }
            50% { transform: translateX(1px) translateY(2px) scale(1.02); }
            100% { transform: translateX(-1px) translateY(-2px) scale(1); }
          }

          @keyframes shimmer {
            0% { transform: translateX(-100%) skewX(-15deg); }
            100% { transform: translateX(200%) skewX(-15deg); }
          }

          @keyframes prism-glow {
            0% { opacity: 0.3; }
            100% { opacity: 0.7; }
          }

          @keyframes edge-glow {
            0% { filter: brightness(1); }
            100% { filter: brightness(1.2); }
          }
        `
      }} />
    </div>
  );
};

export default CSSHolographicCard;